import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { parseAsync } from 'json2csv'
import DetailGpsTrackerMap from './DetailGpsTrackerMap'
import {
  closeIcon,
  iconBatteryFull,
  iconBatteryThreeQuarter,
  iconBatteryHalf,
  iconBatteryLittle,
  iconBatteryEmpty,
  iconBatteryUnknown,
  iconGpsTracker,
} from '../../icons'
import './DetailModalCommon.css'
import './DetailModal.css'
import { getFormattedDate } from '../../helpers/timeHelper'
import { getGpsTrackerDetails } from '../../modules/GpsTracker'
import { getErrorString } from '../../utils/errorUtils'
import Loading from '../common/Loading'

type Props = {
  closeModalFunction: (arg: any | void) => void
  modalProps: {
    tracker: IGpsTracker
    group: IGroup
  }
}

const GPS_TRACKER_INFO_ACQUIRED_HELP = '電池残量などの端末情報を取得できた時刻です。'
const GPS_TRACKER_HEARTBEAT_RECEIVED_HELP = 'サーバーが端末からの定期的な通信を受信した時刻です。'
const GPS_TRACKER_LAST_LOCATION_HELP = '端末の振動や移動により、位置情報を更新した時刻です'
const GPS_TRACKER_CELLULAR_LOCATION_HELP = '端末が携帯基地局の情報を取得した時刻です。'
const GPS_TRACKER_MANUAL_PATH = `${process.env.PUBLIC_URL || ''}/GPS_Manual.pdf`

type StatusLabelWithHelpProps = {
  label: string
  helpText: string
  tooltipId: string
}

const handleBlob = (data: BlobPart[], options: BlobPropertyBag, callback: (url: string) => void) => {
  const url = URL.createObjectURL(new Blob(data, options))
  callback(url)
  URL.revokeObjectURL(url)
}

const StatusLabelWithHelp = ({
  label,
  helpText,
  tooltipId,
}: StatusLabelWithHelpProps) => (
  <span className="gps-tracker-status-label gps-tracker-status-label-with-help">
    <span>{label}</span>
    <span className="gps-tracker-status-help">
      <button
        type="button"
        className="gps-tracker-status-help__trigger"
        aria-label={`${label}の説明`}
        aria-describedby={tooltipId}
      >
        ?
      </button>
      <span
        id={tooltipId}
        className="gps-tracker-status-help__tooltip"
        role="tooltip"
      >
        {helpText}
      </span>
    </span>
  </span>
)

const DetailGpsTrackerModal = ({
  closeModalFunction,
  modalProps,
}: Props) => {
  const { tracker, group } = modalProps
  const [fullTracker, setFullTracker] = useState<IGpsTracker>(tracker)
  const today = useMemo(() => getFormattedDate(new Date(), 'YYYY-MM-DD'), [])
  const [date, setDate] = useState<string>(today)
  const [isLoading, setIsLoading] = useState(false)
  const [jt808Logs, setJt808Logs] = useState<IJT808Log[]>([])
  const isMountedRef = useRef(true)

  const getBatteryIcon = (percentage: number | undefined) => {
    if (percentage === undefined || percentage === null) return iconBatteryUnknown
    if (percentage >= 75) return iconBatteryFull
    if (percentage > 50) return iconBatteryThreeQuarter
    if (percentage >= 25) return iconBatteryHalf
    if (percentage >= 5) return iconBatteryLittle
    return iconBatteryEmpty
  }

  const formatBatteryPercentage = (percentage: number | undefined) => {
    if (percentage === undefined || percentage === null) return '-'
    return `${percentage}%`
  }

  const formatTimestamp = (timestamp?: string | null) => {
    if (!timestamp) return '-'
    return getFormattedDate(new Date(timestamp), 'YYYY/MM/DD HH:mm')
  }

  useEffect(() => () => {
    isMountedRef.current = false
  }, [])

  const updateGpsTrackerLogs = useCallback(async (dateStr: string) => {
    if (!isMountedRef.current) return
    setDate(dateStr)
    try {
      setIsLoading(true)
      const updatedTracker = await getGpsTrackerDetails(
        group.id,
        tracker.device_token,
        { date: dateStr },
      )

      if (!isMountedRef.current) return

      const sanitizedTracker: IGpsTracker = dateStr === today
        ? updatedTracker
        : {
          ...updatedTracker,
          status: undefined,
        }

      setFullTracker(sanitizedTracker)
      setJt808Logs(updatedTracker.jt808_logs || [])
    } catch (error: unknown) {
      if (!isMountedRef.current) return
      console.error(getErrorString(error))
      setJt808Logs([])
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false)
      }
    }
  }, [group.id, today, tracker.device_token])

  const downloadLogs = async () => {
    if (!jt808Logs || jt808Logs.length === 0) {
      return
    }

    const formattedData = jt808Logs.map((log) => ({
      device_token: fullTracker.device_token,
      name: fullTracker.name,
      latitude: log.latitude || '',
      longitude: log.longitude || '',
      speed: log.speed || '',
      ...log,
    }))

    const csv = await parseAsync(formattedData, {
      withBOM: true,
    })

    handleBlob([`${csv}\n`], { type: 'text/csv' }, (url) => {
      const a = document.createElement('a')
      a.href = url
      a.download = `gps_tracker_logs_${date}.csv`
      a.click()
    })
  }

  useEffect(() => {
    updateGpsTrackerLogs(today).catch(() => {
      // updateGpsTrackerLogs内でエラーハンドリング済み
    })
  }, [today, updateGpsTrackerLogs])

  return (
    <div className="detail-modal-container" data-testid="detail-gps-tracker-modal">
      <Loading show={isLoading} />
      <div
        className="overlay"
        onClick={closeModalFunction}
      />
      <div className="detail-base">
        <div className="scrollable">
          <div className="title-row">
            <div className="detail-title">
              GPSトラッカー詳細
            </div>
            <div>
              <button
                onClick={closeModalFunction}
                className="close-button"
                type="button"
              >
                <img
                  src={closeIcon}
                  alt="close-button"
                />
              </button>
            </div>
          </div>
          <div className="detail-modal-content">
            <div className="main-view">
              <div className="icon-wrapper">
                <img className="mamorio-icon" src={iconGpsTracker} alt="gps-tracker" />
              </div>
              <div className="mamorio-name">
                {fullTracker.name}
              </div>
              <div style={{ marginTop: '15px' }}>
                <span style={{ fontSize: '12px', color: '#757575' }}>
                  デバイスID:
                  {' '}
                  {fullTracker.device_token}
                </span>
              </div>
              <div className="gps-tracker-manual-detail">
                <a
                  className="gps-tracker-manual-link"
                  href={GPS_TRACKER_MANUAL_PATH}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GPSトラッカーマニュアル
                </a>
              </div>

              {fullTracker.status && (
                <div className="gps-tracker-status-summary">
                  <div className="mamorio-battery gps-tracker-status-row">
                    <span className="mamorio-battery-title">バッテリー残量</span>
                    <span className="gps-tracker-status-value gps-tracker-battery-value">
                      <img
                        src={getBatteryIcon(fullTracker.status.battery_percentage)}
                        alt="battery"
                        style={{ width: '20px', height: '20px', marginRight: '8px' }}
                      />
                      {formatBatteryPercentage(fullTracker.status.battery_percentage)}
                    </span>
                  </div>
                  <div className="gps-tracker-status-row">
                    <StatusLabelWithHelp
                      label="定期通信"
                      helpText={GPS_TRACKER_HEARTBEAT_RECEIVED_HELP}
                      tooltipId="gps-tracker-detail-heartbeat-received-help"
                    />
                    <span className="gps-tracker-status-value">
                      {formatTimestamp(fullTracker.status.heartbeat_received_at)}
                    </span>
                  </div>
                  <div className="gps-tracker-status-row">
                    <StatusLabelWithHelp
                      label="端末の情報取得"
                      helpText={GPS_TRACKER_INFO_ACQUIRED_HELP}
                      tooltipId="gps-tracker-detail-info-acquired-help"
                    />
                    <span className="gps-tracker-status-value">
                      {formatTimestamp(fullTracker.status.tracked_at)}
                    </span>
                  </div>
                  <div className="gps-tracker-status-row">
                    <StatusLabelWithHelp
                      label="最終GPS位置更新時刻"
                      helpText={GPS_TRACKER_LAST_LOCATION_HELP}
                      tooltipId="gps-tracker-detail-last-location-help"
                    />
                    <span className="gps-tracker-status-value">
                      {formatTimestamp(fullTracker.status.gps_location_updated_at)}
                    </span>
                  </div>
                  <div className="gps-tracker-status-row">
                    <StatusLabelWithHelp
                      label="携帯基地局による位置更新時刻"
                      helpText={GPS_TRACKER_CELLULAR_LOCATION_HELP}
                      tooltipId="gps-tracker-detail-cellular-location-help"
                    />
                    <span className="gps-tracker-status-value">
                      {formatTimestamp(fullTracker.status.cellular?.observed_at)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="detail-view">
            {(jt808Logs && jt808Logs.length > 0) || fullTracker.status ? (
              <DetailGpsTrackerMap
                date={date}
                jt808Logs={jt808Logs}
                updateGpsTrackerLogs={updateGpsTrackerLogs}
                downloadLogs={downloadLogs}
                currentStatus={fullTracker.status}
                detailHistoryEnabled={group.detail_history_enabled}
              />
            ) : (
              <div className="privacy-desc">
                位置情報がありません
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DetailGpsTrackerModal
